import { Fragment, SyntheticEvent } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import i18n from 'src/utils/translate';
import { Community, CurrentUser, Lang, Post } from 'src/types';
import Avatar from 'src/components/avatar';
import CommunityLink from 'src/modules/shared/links/communityLink';
import PostLink from 'src/modules/shared/links/postLink';
import RelativeTime from 'src/components/relativeTime';
import signupContext from 'src/constants/signupContext';
import { showSignupModal } from 'src/modules/shared/modal/actions';
import linkUtils from 'src/utils/linkUtils';
import Badge from 'src/components/badges/badge';
import { trackFootfallEvent } from 'src/utils/footfall';
import EventType from 'src/constants/footfallEventTypes';

type PostItemFooter = {
  currentUser: CurrentUser;
  community: Community;
  post: Post;
  onPostClick: (type: string) => void;
  isSearchPost: boolean;
  isCommunitySearch: boolean;
  hideReplies: boolean;
  lang: Lang;
}

export default function PostItemFooter({currentUser, community, post, onPostClick, isSearchPost, isCommunitySearch, hideReplies, lang}: PostItemFooter) {
  const dispatch = useDispatch();
  const avatarSrc = linkUtils.generateAvatarLinkFromHash(post.author.userImageHash);
  const isVisitorNoRepliesLink = !currentUser && post.totalResponses === 0;

  const onClickUser = (e: SyntheticEvent) => {
    onPostClick('user');
    if (!currentUser) {
      e.preventDefault();
      const context = {
        element: signupContext.element.USER_LINK,
        section: signupContext.section.COMMUNITY_HOME_POST,
      };
      trackFootfallEvent(EventType.CLICKED, {
        clickType: 'sign-up',
        clickMetadata: context,
      });
      dispatch(showSignupModal(context));
    }
  };

  const onClickPost = (e: SyntheticEvent) => {
    onPostClick('post');
    if (isVisitorNoRepliesLink) {
      if (e) e.preventDefault();
      dispatch(showSignupModal({
        section: signupContext.section.COMMUNITY_HOME_POST,
        element: signupContext.element.REPLY,
      }));
    }
  };

  const Replies = () => {
    if (post.isLocked && post.totalResponses === 0) return null;
    return (
      <PostLink
        community={community}
        disabled={isVisitorNoRepliesLink}
        extraArgs={{ responses: '' }}
        post={post}
        onClick={onClickPost}
      >
        {post.totalResponses > 0
          ? <Text>{post.totalResponses} {post.totalResponses === 1 ? i18n.t('Reply') : i18n.t('Replies')}</Text>
          : i18n.t('Be the first to reply')}
      </PostLink>
    );
  };

  return (
    <Container>
      <UserLink href={`/user/${post.author.username}`} onClick={onClickUser}>
        <Avatar size="small" src={avatarSrc}/>
      </UserLink>
      <div>
        <Row>
          <UserLink href={`/user/${post.author.username}`} onClick={onClickUser}>
            {post.author.username}
          </UserLink>
          {post.author.roles?.length > 0 && <Badge content={post.author.roles[0]} />}
          {isSearchPost
            ? !isCommunitySearch &&
              <CommunitySearch>
                {i18n.t('in ')}
                <CommunityLink
                  name={community.name}
                  slug={community.slug}
                  onClick={() => onPostClick('community')}
                />
              </CommunitySearch>
            : (
              <Fragment>
                <Separator>•</Separator><StyledRelativeTime lang={lang} rawTime={post.dateCreated} />
              </Fragment>
            )
          }
        </Row>
        <Row>
          {isSearchPost ? (
            <Fragment>
              <StyledRelativeTime lang={lang} rawTime={post.dateCreated} />
              {!hideReplies && <Fragment><Separator>•</Separator><Replies /></Fragment>}
            </Fragment>
          ) : (
            <Replies />
          )}
        </Row>
      </div>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  margin-top: 15px;
  color: ${({theme}) => theme.colorGreyDark};
`;
const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
`;
const Separator = styled.span`
  margin-right: 8px;
  margin-left: 8px;
  @media (${props => props.theme.underScreenXsmall}) {
    display: none;
  }
`;
const StyledRelativeTime = styled<any>(RelativeTime)`
  margin-right: 8px;
`;
const Text = styled.span`
  color: ${({theme}) => theme.colorGreyDark};
`;
const UserLink = styled.a`
  margin-right: 8px;
  color: ${({theme}) => theme.colorGreyDark};
  &:hover {
    color: ${({theme}) => theme.colorGreyDark};
  }
`;
const CommunitySearch = styled.span`
  margin-left: 8px;
  a {
    color: ${({theme}) => theme.colorGreyDark};
  }
`;
